import { useTranslation } from 'react-i18next';

import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';
import { PDFPage } from 'PDF/components/Page';
import { getColorObjectsStyleByRecord, getColorType } from 'PDF/helpers/documentStyle';

interface ISectionCoverPagePdfProps {
  header: string;
  sectionNumber?: string;
  background: string;
  sectionId: string;
  highlightSettings: {
    position: number;
    variant: 'before' | 'after';
  };
  subChapterNames: string[];
  isEmpty?: boolean;
  isStandardOrLightTariff: boolean,
  isExamplePage: boolean,
}

const colorIndexes = {
  green: [7, 2, 7, 4],
  blue: [0, 2, 3, 4],
  red: [0, 2, 3, 4],
  blueGreen: [6, 2, 3, 4],
  blueRed: [0, 2, 3, 4],
  blueYellow: [0, 35, 3, 4],
};

export const SectionCoverPagePdf = ({
  header,
  sectionNumber,
  background,
  highlightSettings: { position, variant } = {
    position: 0,
    variant: 'before',
  },
  subChapterNames,
  isEmpty = false,
  isStandardOrLightTariff,
  isExamplePage
}: ISectionCoverPagePdfProps) => {
  const [textColor, wrapColor, chaptersColor, sectionColor] = getColorObjectsStyleByRecord(colorIndexes, [
    'color',
    'backgroundColor',
    'backgroundColor',
    'color',
  ]);
  const { t } = useTranslation()

  const colorType = getColorType();

  const renderTitle = () => {
    const splitHeader = header.split(' ');

    if (splitHeader.length <= 1) {
      return <Text>{splitHeader[0]}</Text>;
    }

    const isBefore = variant === 'before';
    const newPosition = isBefore ? position + 1 : position;
    const startStr = splitHeader.slice(0, newPosition).join(' ');
    const endStr = ` ${splitHeader.slice(newPosition).join(' ')}`;

    if (isBefore) {
      return (
        <>
          <Text style={textColor}>{startStr}</Text>
          <Text>{endStr}</Text>
        </>
      );
    }

    return (
      <>
        <Text>{startStr}</Text>
        <Text style={textColor}>{endStr}</Text>
      </>
    );
  };

  return (
    <PDFPage isStandardOrLightTariff={isStandardOrLightTariff}>
      <Text style={styles.header}>{renderTitle()}</Text>
      <View style={[styles.container, wrapColor]}>
        <Image src={background} style={styles.image} />
        <View style={[styles.info, chaptersColor]}>
          <View>
            {isEmpty ? (
              <Text style={[styles.empty, { color: colorType === 'blueYellow' ? '#000000' : '#FFFFFF' }]}>
                {isExamplePage ? 'Please add content in the editor to see it here' : t('pdfPages.pleaseAddContent')}
              </Text>
            ) : (
              <>
                {subChapterNames.map((name) => (
                  <Text
                    key={name}
                    style={[styles.chapter, { color: colorType === 'blueYellow' ? '#000000' : '#FFFFFF' }]}
                  >
                    {name}
                  </Text>
                ))}
              </>
            )}
          </View>
          <View>{!isEmpty && <Text style={[styles.sectionNumber, sectionColor]}>{sectionNumber}</Text>}</View>
        </View>
      </View>
    </PDFPage>
  );
};

const styles = StyleSheet.create({
  header: {
    marginTop: -7,
    fontFamily: 'Noto',
    fontWeight: 600,
    fontSize: 25,
    marginBottom: 7,
  },
  container: {
    display: 'flex',
    maxHeight: 715,
    padding: 27,
  },
  image: {
    flexShrink: 2,
    paddingTop: 5,
    borderTop: '1px solid #212121',
  },
  info: {
    flexGrow: 2,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 5,
    minHeight: 173,
    marginTop: 10,
    padding: '5px 19px',
  },
  chapter: {
    fontFamily: 'Noto',
    fontSize: 13,
    fontWeight: 300,
  },
  sectionNumber: {
    fontFamily: 'Noto',
    fontWeight: 300,
    fontSize: 81,
  },
  empty: {
    fontFamily: 'Noto',
    fontWeight: 300,
    fontSize: 20,
  },
});
